import { FC, SyntheticEvent } from "react";
import {
  Checkbox, TableCell, TableRow, styled, tableCellClasses,
  AccordionSummary, List, ListItem, ListItemText, Typography, Accordion,
  AccordionDetails, Stack, Box, Divider, Card, CardContent, Chip, Tooltip,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditIcon from "@mui/icons-material/Edit";
import {
  DataFacture, STATUT_VALIDEE, STATUT_ENVOYEE,
  formatCaGlobalHt, formatPeriode, formatDateFr
} from "./TableauDeBordFacturation";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    textAlign: "center",
    color: "#fff",
  },
}));

interface RowTableauDeBordFacturationProps {
  isDesktopOrLaptop: boolean;
  facture: DataFacture;
  /**
   * La ligne peut être cochée pour une action en masse (envoi au client, ou
   * validation pour les profils autorisés), cf. TableauDeBordFacturation.
   */
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  /** Ouvre le pop-up d'ajout / modification de la date de validation */
  onEditDateValidation: (facture: DataFacture) => void;
  expanded: string | false;
  setExpanded: React.Dispatch<React.SetStateAction<string | false>>;
}

const RowTableauDeBordFacturation: FC<RowTableauDeBordFacturationProps> = ({
  isDesktopOrLaptop,
  facture,
  selectable,
  selected,
  onToggleSelect,
  onEditDateValidation,
  expanded,
  setExpanded,
}) => {
  // Une facture sans action possible (déjà envoyée, ou non validée pour un
  // profil qui ne peut pas valider) ne peut pas être sélectionnée : sa case
  // est cochée et grisée (désactivée).
  const nonSelectionnable = !selectable;

  const dateEnvoi = formatDateFr(facture?.date_envoi);

  // Date de dernière validation des saisies de la ligne : "-" tant que la
  // ligne n'est pas entièrement validée (cf. ServiceFacturation).
  const dateValidation = formatDateFr(facture?.date_validation);

  const erreurs: string[] = facture?.erreurs ?? [];

  // Crayon d'ajout / modification de la date de validation. Disponible sur
  // toutes les lignes : le "-" d'une ligne non validée doit pouvoir être
  // renseigné, et une date déjà saisie doit rester corrigeable.
  const boutonEditDateValidation = (
    <Tooltip
      title={
        facture?.date_validation
          ? "Modifier la date de validation"
          : "Ajouter une date de validation"
      }
    >
      <IconButton
        size="small"
        aria-label="modifier la date de validation"
        sx={{ color: "#FC8404", p: 0.25, ml: 0.5 }}
        onClick={(e) => {
          e.stopPropagation();
          onEditDateValidation(facture);
        }}
      >
        <EditIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );

  // Validée = vert, Envoyée = bleu, Non validée = orange
  const statutBgColor =
    facture?.statut === STATUT_VALIDEE
      ? "#2E7D32"
      : facture?.statut === STATUT_ENVOYEE
        ? "#1976D2"
        : "#FC8404";

  const statutChip = (
    <Chip
      label={facture?.statut}
      size="small"
      sx={{
        fontSize: 11,
        color: "#fff",
        bgcolor: statutBgColor,
      }}
    />
  );

  // Signale les lignes dont le CA n'a pas pu être entièrement converti
  // (taux de change manquant, cf. ServiceFacturation::cumulerMontantDashBoard)
  const alerteErreurs = erreurs.length > 0 && (
    <Tooltip title={erreurs.join(" — ")}>
      <WarningAmberIcon sx={{ fontSize: 14, color: "#FC8404", ml: 0.5 }} />
    </Tooltip>
  );

  const handleChangeAccordionValue =
    (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <>
      {isDesktopOrLaptop ? (
        <TableRow>
          <StyledTableCell>
            <Checkbox
              size="small"
              color="warning"
              sx={{ color: "#fff" }}
              checked={selected || nonSelectionnable}
              disabled={nonSelectionnable}
              onChange={() => onToggleSelect(facture?.id)}
            />
          </StyledTableCell>
          <StyledTableCell>{facture?.societe_facturation_libelle}</StyledTableCell>
          <StyledTableCell>{facture?.bu_libelle}</StyledTableCell>
          <StyledTableCell>{facture?.client_libelle}</StyledTableCell>
          <StyledTableCell>{facture?.operation_libelle}</StyledTableCell>
          <StyledTableCell>{facture?.code_gesco_libelle}</StyledTableCell>
          <StyledTableCell>
            {formatPeriode(facture?.periode_mois, facture?.periode_annee)}
          </StyledTableCell>
          <StyledTableCell>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
            >
              {formatCaGlobalHt(facture?.ca_global_ht)}
              {alerteErreurs}
            </Stack>
          </StyledTableCell>
          <StyledTableCell>{facture?.devise_libelle}</StyledTableCell>
          <StyledTableCell>{statutChip}</StyledTableCell>
          <StyledTableCell>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
            >
              {dateValidation}
              {boutonEditDateValidation}
            </Stack>
          </StyledTableCell>
          <StyledTableCell>{dateEnvoi}</StyledTableCell>
        </TableRow>
      ) : (
        <Accordion
          expanded={expanded === facture?.id}
          onChange={handleChangeAccordionValue(facture?.id)}
          sx={{
            borderRadius: 1,
            bgcolor: "#fff",
            mb: 1,
            ml: 0.1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#080d24" }} />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Stack direction="row" alignItems="center">
              <Checkbox
                size="small"
                color="warning"
                checked={selected || nonSelectionnable}
                disabled={nonSelectionnable}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSelect(facture?.id)}
              />
              <List sx={{ py: 0 }} dense>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Client :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {facture?.client_libelle}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Opération :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {facture?.operation_libelle}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Période de facturation :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {formatPeriode(
                          facture?.periode_mois,
                          facture?.periode_annee
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="CA Globale HT :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {formatCaGlobalHt(facture?.ca_global_ht)}{" "}
                        {facture?.devise_libelle ?? ""}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText primary="Statut :" secondary={statutChip} />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Date de validation :"
                    secondary={
                      <Stack
                        component="span"
                        direction="row"
                        alignItems="center"
                      >
                        <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                          {dateValidation}
                        </Typography>
                        {boutonEditDateValidation}
                      </Stack>
                    }
                  />
                </ListItem>
              </List>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              <Box>
                <Box mt={2}>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="div"
                    sx={{ mb: 0 }}
                  >
                    Détails
                  </Typography>
                  <Divider
                    orientation="horizontal"
                    sx={{ bgcolor: "#fff", mb: 1 }}
                  />
                </Box>
              </Box>

              <Card>
                <CardContent>
                  <List dense>
                    <ListItem dense>
                      <ListItemText
                        primary="Société de facturation :"
                        secondary={facture?.societe_facturation_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Bu :"
                        secondary={facture?.bu_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Code Gesco :"
                        secondary={facture?.code_gesco_libelle}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Intitulé tiers :"
                        secondary={facture?.intitule_tiers ?? "-"}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Devise de facturation :"
                        secondary={facture?.devise_libelle ?? ""}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="CA Globale HT (devise compta) :"
                        secondary={`${formatCaGlobalHt(
                          facture?.ca_global_ht
                        )} ${facture?.devise_libelle ?? ""}`}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Taux de change appliqué :"
                        secondary={facture?.taux ?? "-"}
                      />
                    </ListItem>
                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Date d'envoi de la facture :"
                        secondary={dateEnvoi}
                      />
                    </ListItem>
                  </List>

                  {erreurs.length > 0 && (
                    <List dense>
                      {erreurs.map((erreur) => (
                        <ListItem key={erreur} dense>
                          <WarningAmberIcon
                            sx={{ fontSize: 16, color: "#FC8404", mr: 1 }}
                          />
                          <ListItemText
                            secondary={
                              <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                                {erreur}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default RowTableauDeBordFacturation;
